import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styles from '../AdminTrackVideos.module.css';
import {
  createAdminContent,
  deleteAdminContent,
  updateAdminContent,
} from '../../utils/api';
import {
  actionsFromContent,
  asActionsArray,
  ContentCoverPreview,
  ContentVideoPreview,
  FeedbackBanner,
  getContentCoverUrl,
  getContentVideoUrl,
  useCoverPreview,
  useVideoPreview,
} from './helpers';

function portalToBody(node) {
  if (typeof document === 'undefined') return null;
  return createPortal(node, document.body);
}

function Field({ id, label, required, optional, children }) {
  return (
    <div className={styles['field']}>
      <label className={styles['label']} htmlFor={id}>
        {label}{' '}
        {required ? <span className={styles['required']}>*</span> : null}
        {optional ? <span className={styles['optional']}>(optional)</span> : null}
      </label>
      {children}
    </div>
  );
}

function DialogActions({ onCancel, submitLabel, submitting, submitBusyLabel, danger, onSubmitClick }) {
  return (
    <div className={styles['dialog-actions']}>
      <button type="button" className={styles['secondary']} onClick={onCancel} disabled={submitting}>
        Cancel
      </button>
      {onSubmitClick ? (
        <button
          type="button"
          className={danger ? `${styles['primary']} ${styles['primary-danger']}` : styles['primary']}
          onClick={onSubmitClick}
          disabled={submitting}
          aria-busy={submitting}
        >
          {submitting ? submitBusyLabel : submitLabel}
        </button>
      ) : (
        <button type="submit" className={styles['primary']} disabled={submitting} aria-busy={submitting}>
          {submitting ? submitBusyLabel : submitLabel}
        </button>
      )}
    </div>
  );
}

function useContentForm(item) {
  const [title, setTitle] = useState(() => String(item?.title ?? ''));
  const [description, setDescription] = useState(() => String(item?.description ?? ''));
  const [actions, setActions] = useState(() => actionsFromContent(item));
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const cover = useCoverPreview();
  const video = useVideoPreview();

  const handleCoverChange = (event) => {
    const error = cover.handleChange(event);
    setFeedback(error ? { type: 'error', message: error } : null);
  };

  const handleVideoChange = (event) => {
    const error = video.handleChange(event);
    setFeedback(error ? { type: 'error', message: error } : null);
  };

  const updateAction = (index, value) => {
    setActions((current) => current.map((action, actionIndex) => (actionIndex === index ? value : action)));
  };

  const addAction = () => setActions((current) => [...current, '']);

  const removeAction = (index) => {
    setActions((current) => {
      const next = current.filter((_, actionIndex) => actionIndex !== index);
      return next.length ? next : [''];
    });
  };

  return {
    title,
    setTitle,
    description,
    setDescription,
    actions,
    updateAction,
    addAction,
    removeAction,
    cover,
    handleCoverChange,
    video,
    handleVideoChange,
    submitting,
    setSubmitting,
    feedback,
    setFeedback,
  };
}

function CoverFileRow({ id, fileName, fallbackName, onChange, inputKey = 0, action = null }) {
  return (
    <div className={styles['file-row']}>
      <input key={inputKey} id={id} type="file" accept="image/*" className={styles['file-input']} onChange={onChange} />
      <label htmlFor={id} className={styles['file-button']}>Choose Image</label>
      <span className={styles['file-name']}>{fileName || fallbackName}</span>
      {action}
    </div>
  );
}

function VideoFileRow({ id, fileName, fallbackName, onChange }) {
  return (
    <div className={styles['file-row']}>
      <input id={id} type="file" accept="video/*" className={styles['file-input']} onChange={onChange} />
      <label htmlFor={id} className={styles['file-button']}>Choose Video</label>
      <span className={styles['file-name']}>{fileName || fallbackName}</span>
    </div>
  );
}

function CoverPreviewCol({ title, url, emptyText, alt }) {
  return (
    <div className={styles['upload-preview-col']}>
      <h3 className={styles['upload-preview-title']}>{title}</h3>
      <div className={styles['upload-preview-box']}>
        <ContentCoverPreview url={url} emptyText={emptyText} alt={alt} />
      </div>
    </div>
  );
}

function VideoPreviewCol({ title, url, emptyText }) {
  return (
    <div className={styles['upload-preview-col']}>
      <h3 className={styles['upload-preview-title']}>{title}</h3>
      <div className={styles['upload-preview-box']}>
        <ContentVideoPreview url={url} emptyText={emptyText} />
      </div>
    </div>
  );
}

function ActionsField({ idPrefix, actions, onAdd, onChange, onRemove }) {
  return (
    <div className={styles['field']}>
      <div className={styles['actions-field-header']}>
        <label className={styles['label']} htmlFor={`${idPrefix}-action-0`}>Action Items</label>
        <button type="button" className={styles['add-action-button']} onClick={onAdd}>
          <span aria-hidden>+</span>
          Add Action Item
        </button>
      </div>
      <div className={styles['action-inputs']}>
        {actions.map((action, index) => (
          <div key={`${idPrefix}-action-${index}`} className={styles['action-input-row']}>
            <textarea
              id={`${idPrefix}-action-${index}`}
              className={`${styles['textarea']} ${styles['textarea-action']}`}
              rows={3}
              value={action}
              onChange={(event) => onChange(index, event.target.value)}
              placeholder={`Action item ${index + 1}`}
            />
            <button
              type="button"
              className={styles['remove-action-button']}
              onClick={() => onRemove(index)}
              aria-label={`Remove action item ${index + 1}`}
              disabled={actions.length === 1 && !action.trim()}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContentFields({ idPrefix, form }) {
  return (
    <>
      <Field id={`${idPrefix}-title`} label="Title" required>
        <input
          id={`${idPrefix}-title`}
          className={styles['text-input']}
          value={form.title}
          onChange={(event) => form.setTitle(event.target.value)}
          autoComplete="off"
          required
        />
      </Field>
      <Field id={`${idPrefix}-description`} label="Description">
        <textarea
          id={`${idPrefix}-description`}
          className={styles['textarea']}
          rows={2}
          value={form.description}
          onChange={(event) => form.setDescription(event.target.value)}
        />
      </Field>
      <ActionsField
        idPrefix={idPrefix}
        actions={form.actions}
        onAdd={form.addAction}
        onChange={form.updateAction}
        onRemove={form.removeAction}
      />
    </>
  );
}

export function CreateContentModal({ categoryId, navigate, onClose, onCreated }) {
  const form = useContentForm();
  const coverPreviewUrl = form.cover.previewUrl && form.cover.file ? form.cover.previewUrl : null;
  const previewUrl = form.video.previewUrl && form.video.file ? form.video.previewUrl : null;

  const submit = async (event) => {
    event.preventDefault();
    if (!categoryId) return;
    if (!form.title.trim()) return form.setFeedback({ type: 'error', message: 'Title is required.' });
    if (!form.video.file) return form.setFeedback({ type: 'error', message: 'Video file is required.' });

    form.setSubmitting(true);
    form.setFeedback(null);
    const result = await createAdminContent(
      {
        categoryId,
        coverImageFile: form.cover.file,
        videoFile: form.video.file,
        title: form.title,
        description: form.description,
        actions: asActionsArray(form.actions),
      },
      navigate
    );
    form.setSubmitting(false);
    if (!result.ok) {
      form.setFeedback({ type: 'error', message: result.message || 'Create failed.' });
      return;
    }
    onCreated(result.message);
  };

  return (
    <div className={styles['sheet-overlay']} role="presentation">
      <div className={styles['sheet']} role="dialog" aria-modal="true" aria-labelledby="admin-content-create-title">
        <div className={styles['sheet-header']}>
          <h2 id="admin-content-create-title" className={styles['sheet-title']}>Create Quick Richuel</h2>
          <button type="button" className={styles['dialog-close']} onClick={onClose} aria-label="Close create dialog">×</button>
        </div>
        <form className={styles['sheet-body']} onSubmit={submit}>
          <Field id="create-cover" label="Cover Image" optional>
            <CoverFileRow id="create-cover" fileName={form.cover.file?.name} fallbackName="No file selected" onChange={form.handleCoverChange} />
            <div className={styles['upload-preview-grid']}>
              <CoverPreviewCol title="New Cover Preview" url={coverPreviewUrl} emptyText="Select an image to preview" alt="Cover preview" />
            </div>
          </Field>
          <Field id="create-video" label="Video" required>
            <VideoFileRow id="create-video" fileName={form.video.file?.name} fallbackName="No file selected" onChange={form.handleVideoChange} />
            <div className={styles['upload-preview-grid']}>
              <VideoPreviewCol title="New Video Preview" url={previewUrl} emptyText="Select a video to preview" />
            </div>
          </Field>
          <ContentFields idPrefix="create" form={form} />
          <FeedbackBanner feedback={form.feedback} />
          <DialogActions
            onCancel={onClose}
            submitLabel="Create"
            submitBusyLabel="Creating…"
            submitting={form.submitting}
          />
        </form>
      </div>
    </div>
  );
}

export function EditContentModal({ item, categoryId, navigate, onClose, onSaved }) {
  const form = useContentForm(item);
  const [removeCover, setRemoveCover] = useState(false);
  const [coverInputKey, setCoverInputKey] = useState(0);
  const existingCoverUrl = getContentCoverUrl(item);
  const newCoverUrl = form.cover.previewUrl && form.cover.file ? form.cover.previewUrl : null;
  const currentCoverUrl = removeCover ? null : existingCoverUrl;
  const existingVideoUrl = getContentVideoUrl(item);
  const newVideoUrl = form.video.previewUrl && form.video.file ? form.video.previewUrl : null;
  const canRemoveCover = Boolean(existingCoverUrl) && !removeCover;

  const handleEditCoverChange = (event) => {
    form.handleCoverChange(event);
    if (event.target.files?.[0]?.type?.startsWith('image/')) setRemoveCover(false);
  };

  const handleRemoveCover = () => {
    form.cover.clear();
    setCoverInputKey((key) => key + 1);
    setRemoveCover(true);
    form.setFeedback(null);
  };

  const submit = async (event) => {
    event.preventDefault();
    const id = String(item?.id ?? '').trim();
    if (!id || !categoryId) return;
    if (!form.title.trim()) return form.setFeedback({ type: 'error', message: 'Title is required.' });

    form.setSubmitting(true);
    form.setFeedback(null);
    const result = await updateAdminContent(
      {
        id,
        categoryId,
        coverImageFile: removeCover ? null : form.cover.file,
        removeCover: removeCover && !form.cover.file,
        videoFile: form.video.file,
        title: form.title,
        description: form.description,
        actions: asActionsArray(form.actions),
      },
      navigate
    );
    form.setSubmitting(false);
    if (!result.ok) {
      form.setFeedback({ type: 'error', message: result.message || 'Update failed.' });
      return;
    }
    onSaved(result.message);
  };

  return portalToBody(
    <div className={styles['dialog-overlay']} role="presentation">
      <div className={styles['dialog']} role="dialog" aria-modal="true" aria-labelledby="admin-content-edit-title">
        <div className={styles['dialog-header']}>
          <h2 id="admin-content-edit-title" className={styles['dialog-title']}>Edit Quick Richuel</h2>
          <button type="button" className={styles['dialog-close']} onClick={onClose} aria-label="Close edit Quick Richuel dialog">×</button>
        </div>
        <form className={styles['dialog-body']} onSubmit={submit}>
          <ContentFields idPrefix="edit" form={form} />
          <Field id="edit-cover" label="Cover Image" optional>
            <CoverFileRow
              id="edit-cover"
              inputKey={coverInputKey}
              fileName={form.cover.file?.name}
              fallbackName={removeCover ? 'Cover will be removed' : 'Keep existing cover'}
              onChange={handleEditCoverChange}
              action={canRemoveCover ? (
                <button
                  type="button"
                  className={styles['remove-action-button']}
                  onClick={handleRemoveCover}
                  disabled={form.submitting}
                >
                  Remove cover
                </button>
              ) : removeCover ? (
                <button
                  type="button"
                  className={styles['remove-action-button']}
                  onClick={() => setRemoveCover(false)}
                  disabled={form.submitting}
                >
                  Keep cover
                </button>
              ) : null}
            />
            <div className={styles['upload-preview-grid']}>
              <CoverPreviewCol
                title="Current Cover"
                url={currentCoverUrl}
                emptyText={removeCover ? 'Cover will be removed' : 'No current cover'}
                alt="Current cover"
              />
              <CoverPreviewCol title="New Cover Preview" url={newCoverUrl} emptyText="Select an image to replace (optional)" alt="New cover preview" />
            </div>
          </Field>
          <Field id="edit-video" label="Replace Video" optional>
            <VideoFileRow id="edit-video" fileName={form.video.file?.name} fallbackName="Keep existing video" onChange={form.handleVideoChange} />
            <div className={styles['upload-preview-grid']}>
              <VideoPreviewCol title="Current Video" url={existingVideoUrl} emptyText="No current video" />
              <VideoPreviewCol title="New Video Preview" url={newVideoUrl} emptyText="Select a video to replace (optional)" />
            </div>
          </Field>
          <FeedbackBanner feedback={form.feedback} />
          <DialogActions
            onCancel={onClose}
            submitLabel="Save"
            submitBusyLabel="Saving…"
            submitting={form.submitting}
          />
        </form>
      </div>
    </div>
  );
}

export function DeleteContentModal({ item, navigate, onClose, onDeleted }) {
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const confirmDelete = async () => {
    const id = String(item?.id ?? '').trim();
    if (!id) return;
    setSubmitting(true);
    setFeedback(null);
    const result = await deleteAdminContent({ id }, navigate);
    setSubmitting(false);
    if (!result.ok) {
      setFeedback({ type: 'error', message: result.message || 'Delete failed.' });
      return;
    }
    onDeleted(result.message);
  };

  return portalToBody(
    <div className={styles['dialog-overlay']} role="presentation">
      <div className={`${styles['dialog']} ${styles['dialog-danger']}`} role="dialog" aria-modal="true" aria-labelledby="admin-content-delete-title">
        <div className={styles['dialog-header']}>
          <h2 id="admin-content-delete-title" className={styles['dialog-title']}>Delete Quick Richuel</h2>
          <button type="button" className={styles['dialog-close']} onClick={onClose} aria-label="Close delete dialog">×</button>
        </div>
        <div className={styles['dialog-body']}>
          <p className={styles['delete-text']}>Delete this Quick Richuel permanently from the active list?</p>
          <FeedbackBanner feedback={feedback} />
          <DialogActions
            onCancel={onClose}
            submitLabel="Delete"
            submitBusyLabel="Deleting…"
            submitting={submitting}
            danger
            onSubmitClick={confirmDelete}
          />
        </div>
      </div>
    </div>
  );
}
